<template>
  <v-form
    ref="form"
    v-model="isFormValid"
    class="general-info mt-3"
    :lazy-validation="true"
  >
    <p>{{ $t("room.info.description") }}</p>
    <niko-slider
      :min="1"
      :max="100"
      :label="$t('room.info.longer')"
      suffix="m"
      step="1"
      :accuracy="0.1"
      :value="longer_size_length"
      :key="sliderKey"
      textFieldType="text"
      @onChange="
        (valueObj) => {
          setRoomLength(valueObj.value);
          if (!valueObj.preventRerender) {
            incrementKey();
          }
        }
      "
    ></niko-slider>
    <v-subheader
      class="mb-1 ml-0 pl-0"
      style="height: auto; color: rgba(255, 255, 255, 0.6)"
    >
      {{ $t("room.info.shorter") }}
    </v-subheader>
    <v-text-field
      :value="shorterSize"
      class="text-field ml-2"
      rounded
      outlined
      disabled
      hide-details
      filled
      suffix="m"
    ></v-text-field>
    <v-subheader class="mt-6 mb-1 ml-0 pl-0" style="height: auto">
      {{ $t("room.info.name") }}
    </v-subheader>
    <v-text-field
      class="mb-1"
      rounded
      outlined
      :rules="[
        (v) => !!v || $t('room.error_message.name_required'),
        (v) =>
          (v && v.length <= 50) || $t('room.error_message.length_requirement'),
        (v) => isRoomNameUnique(v) || $t('room.error_message.room_exists'),
      ]"
      required
      filled
      :value="name"
      @change="(value) => handleNameChange(value)"
      :hide-details="
        isFormValid || (!!name && name.length <= 50 && isRoomNameUnique(name))
      "
    ></v-text-field>

    <v-subheader class="mb-1 ml-0 pl-0 pt-3" style="height: auto">
      {{ room_use_label }}
    </v-subheader>
    <room-type-dropdown
      :items="roomTypes"
      :placeholder="room_use_label"
      :value="
        room_types.find((room_type) => room_type.room_type_id === room_type_id)
      "
      item-text="label"
      @onSelect="(value) => setRoomType(value.room_type_id)"
    />

    <v-subheader class="mb-1 mt-1 ml-0 pl-0 pt-3" style="height: auto">
      {{ $t("room.info.comment") }}
    </v-subheader>
    <niko-textarea
      :rows="1"
      :value="room_info_comment"
      @onChange="(value) => setRoomInfoComment(value)"
    />
    <guidance-button page="configuration.room_general_info" bottom="106" />
  </v-form>
</template>
<script>
import { mapMutations, mapState } from "vuex";
import { flattenDeep } from "lodash";
import { eventBus } from "@/components/EventBus";
import NikoTextarea from "@/components/UI/NikoTextarea";
import NikoSlider from "@/components/UI/NikoSlider";
import GuidanceButton from "@/components/guidance/GuidanceButton";
import RoomTypeDropdown from "@/components/sidebar/RoomTypeDropdown";
import { calculateRectangleProperties } from "../plan/roomPropertiesHelpers";

export default {
  name: "room-general-info-tab",
  components: {
    NikoSlider,
    NikoTextarea,
    GuidanceButton,
    RoomTypeDropdown,
  },
  computed: {
    ...mapState("roomStore", [
      "room_id",
      "name",
      "room_type",
      "room_type_id",
      "room_info_comment",
      "corners",
      "room_types",
      "longer_size_length",
    ]),
    ...mapState("projectStore", [
      "buildings",
      "standard",
      "available_standards",
    ]),
    room_use_label() {
      const standard_name = this.available_standards.find(
        (item) => item.standard_id === this.standard
      ).standard_name;
      return this.$t("room.info.use_per_standard", { standard: standard_name });
    },
    shorterSize() {
      if (this.corners.length >= 6) {
        const { shorterSideInMeters } = calculateRectangleProperties(
          this.corners,
          this.longer_size_length
        );
        return shorterSideInMeters.toFixed(1);
      }
      return 0;
    },
    roomTypes() {
      return this.room_types?.reduce((prev, curr, idx) => {
        const currGroup = curr.group_name;
        const prevGroup =
          this.room_types[idx - 1]?.group_name || this.room_types[0].group_name;
        return [
          ...prev,
          ...(currGroup !== prevGroup && !curr.top
            ? [{ header: currGroup }]
            : []),
          {
            room: curr,
            value: curr,
            label: `${curr.type_id} ${curr.room_type}`,
          },
        ];
      }, []);
    },
  },
  data: () => ({
    isFormValid: true,
    hideErrors: true,
    sliderKey: 1,
  }),
  methods: {
    ...mapMutations("roomStore", [
      "setRoomName",
      "setRoomType",
      "setRoomInfoComment",
      "setRoomLength",
      "setIsRoomValid",
      "resetRoomState",
    ]),
    incrementKey() {
      this.sliderKey += 1;
    },
    handleNameChange(value) {
      this.setRoomName(value);
    },
    isRoomNameUnique(name) {
      const floors = flattenDeep(
        this.buildings.map((building) => building.floors)
      );

      const rooms = flattenDeep(
        floors
          .filter((floor) => floor.rooms?.length > 0)
          .map((floor) => floor.rooms)
      );

      const roomNames = flattenDeep(
        rooms
          .filter((room) => room.room_id !== this.room_id)
          .map((room) => room.name)
      );

      return !roomNames.includes(name);
    },
    clearRoomData() {
      this.$refs.form ? this.$refs.form.resetValidation() : null;
      this.resetRoomState();
    },
    validate() {
      this.$refs.form && this.$refs.form.validate();
    },
  },
  created() {
    eventBus.$on("clearRoomData", () => this.clearRoomData());
    eventBus.$on("validateRoomForm", () => this.validate());
    eventBus.$on("resetValidation", () => {
      this.$refs.form ? this.$refs.form.resetValidation() : null;
    });
  },
  destroyed() {
    eventBus.$off("clearRoomData");
    eventBus.$off("validateRoomForm");
    eventBus.$off("resetValidation");
  },
  watch: {
    isFormValid(value) {
      this.setIsRoomValid(value);
    },
  },
};
</script>
<style lang="scss">
.general-info {
  .text-field {
    width: 100px;
  }
  .v-text-field--rounded > .v-input__control > .v-input__slot {
    padding: 0 16px;
  }
}

.v-text-field--full-width .v-input__append-inner,
.v-text-field--enclosed .v-input__append-inner {
  margin-top: 7px !important;
}
</style>
